import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import Box from '@mui/material/Box';

import { AppProvider, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import { useAuth } from 'contexts/AuthContext';
import { NoticeCountProvider } from 'contexts/NoticeCountContext';

import AppTitle from 'components/layout/AppTitle';
import ToolbarAccount from 'components/layout/ToolbarAccount';
import {
  NoticeToolbarBadge,
  dashboardTheme,
  handleAddAccount,
  handleSwitchAccount,
  signInOut,
  toSession,
} from 'layouts/router/common';
import ScreenGuard from 'routes/ScreenGuard';

/**
 * チケットドメインのレイアウト。
 * - Workspace/DevDocs と同じくサイドバーナビゲーションは表示しない（hideNavigation・navigation={[]}）。
 * - 画面遷移はダッシュボード（Home）と一覧のUI/UX＋各画面の DomainBreadcrumbs で行う。
 * - 全ユーザーアクセス可（ScreenGuard は付けるが ROLE_SCREEN_MAP で全ロールに付与済み）。
 */
const TicketLayout: React.FC = () => {
  const { userSession } = useAuth();

  const router = useRouter('/ticket');

  const authentication = React.useMemo(() => signInOut, []);

  const ToolbarAccountSlot = React.useCallback(
    () => (
      <ToolbarAccount
        userSession={userSession}
        onSignOut={signInOut.signOut}
        onSwitchAccount={handleSwitchAccount}
        onAddAccount={handleAddAccount}
      />
    ),
    [userSession],
  );

  const ToolbarActionsSlot = React.useCallback(
    () => (
      <NoticeToolbarBadge
        allowedScreens={userSession.allowedScreens}
        userName={userSession.userName}
      />
    ),
    [userSession.allowedScreens, userSession.userName],
  );

  return (
    <NoticeCountProvider>
      <AppProvider
        session={toSession(userSession)}
        authentication={authentication}
        navigation={[]}
        router={router}
        theme={dashboardTheme}
      >
        <DashboardLayout
          hideNavigation
          slots={{
            appTitle: TicketAppTitle,
            toolbarActions: ToolbarActionsSlot,
            toolbarAccount: ToolbarAccountSlot,
          }}
        >
          <Box
            sx={{
              pt: 1.5,
              pl: 2,
              pr: 2,
              pb: 1.5,
              height: 'calc(100vh - 64px)',
              overflow: 'auto',
            }}
          >
            <ScreenGuard allowedScreens={userSession.allowedScreens}>
              <Outlet />
            </ScreenGuard>
          </Box>
        </DashboardLayout>
      </AppProvider>
    </NoticeCountProvider>
  );
};

function useRouter(initialPath: string): Router {
  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => {
        navigate(String(path));
        setPathname(String(path));
      },
    };
  }, [pathname, navigate]);

  return router;
}

function TicketAppTitle() {
  return <AppTitle icon={<ConfirmationNumberIcon />} title="チケット管理" color="#c2185b" />;
}

export default TicketLayout;
